/**
 * AdminBackgroundChecks — unified /admin/background-checks table.
 *
 * Aggregates employer-requested screenings + candidate-initiated PBGs
 * into one paginated view. Filters: status, employer, package, source,
 * date range, freeform search. CSV export (Compliance/Platform only).
 *
 * Role-aware rendering: rows are already shaped by the backend
 * (`shape_bg_check_for_role`) so Support tier never sees PII surfaces.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Download, RefreshCw, ShieldCheck, Filter, Search as SearchIcon, ArrowUpRight, Flag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STATUS_TONE = {
    clear: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40",
    consider: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40",
    consider_review_required: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40",
    complete_with_canceled: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/40",
    dispute: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/40",
    in_progress: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40",
    invitation_sent: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40",
    consent_pending: "bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/40",
    canceled: "bg-muted text-muted-foreground border-border",
    expired: "bg-muted text-muted-foreground border-border",
};

const ResultBadge = ({ status, label }) => {
    const tone = STATUS_TONE[(status || "").toLowerCase()] || "bg-muted text-foreground border-border";
    return (
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
            {label || status}
        </span>
    );
};

const SourceBadge = ({ source }) => (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        source === "candidate_initiated"
            ? "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400"
            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
    }`}>
        {source === "candidate_initiated" ? "Talent-initiated" : "Employer"}
    </span>
);

export default function AdminBackgroundChecks() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState("");
    const [canSeeDetails, setCanSeeDetails] = useState(false);
    const [filters, setFilters] = useState({ statuses: [], packages: [], employers: [] });

    // Filter state (hydrated from URL for deep-link)
    const [status, setStatus] = useState(() => searchParams.get("status") || "");
    const [employer, setEmployer] = useState(() => searchParams.get("employer") || "");
    const [pkg, setPkg] = useState(() => searchParams.get("package") || "");
    const [source, setSource] = useState(() => searchParams.get("source") || "");
    const [dateFrom, setDateFrom] = useState(() => searchParams.get("date_from") || "");
    const [dateTo, setDateTo] = useState(() => searchParams.get("date_to") || "");
    const [q, setQ] = useState(() => searchParams.get("q") || "");

    // Sync active filters → URL so admins can share deep-links.
    useEffect(() => {
        const p = new URLSearchParams();
        if (status) p.set("status", status);
        if (employer) p.set("employer", employer);
        if (pkg) p.set("package", pkg);
        if (source) p.set("source", source);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        if (q) p.set("q", q);
        setSearchParams(p, { replace: true });
    }, [status, employer, pkg, source, dateFrom, dateTo, q, setSearchParams]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            if (status) params.status = status;
            if (employer) params.employer = employer;
            if (pkg) params.package = pkg;
            if (source) params.source = source;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (q) params.q = q;
            const { data } = await api.get("/admin/background-checks", { params });
            setRows(data.results || []);
            setTotal(data.total || 0);
            setRole(data.admin_role || "");
            setCanSeeDetails(!!data.can_see_details);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load background checks.");
        } finally {
            setLoading(false);
        }
    }, [status, employer, pkg, source, dateFrom, dateTo, q, page, pageSize]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/admin/background-checks/filters")
            .then(({ data }) => setFilters(data || {}))
            .catch(() => {});
    }, []);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    const exportCsv = async () => {
        try {
            const params = new URLSearchParams();
            if (status) params.set("status", status);
            if (employer) params.set("employer", employer);
            if (pkg) params.set("package", pkg);
            if (source) params.set("source", source);
            if (dateFrom) params.set("date_from", dateFrom);
            if (dateTo) params.set("date_to", dateTo);
            if (q) params.set("q", q);
            const url = `/admin/background-checks/export.csv?${params.toString()}`;
            const r = await api.get(url, { responseType: "blob" });
            const blob = new Blob([r.data], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `background-checks-${new Date().toISOString().slice(0, 10)}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success("CSV export downloaded.");
        } catch (err) {
            const msg = err?.response?.status === 403
                ? "CSV export is limited to Compliance role admins."
                : (err?.response?.data?.detail || "Export failed.");
            toast.error(msg);
        }
    };

    const clearAll = () => {
        setStatus(""); setEmployer(""); setPkg(""); setSource("");
        setDateFrom(""); setDateTo(""); setQ("");
    };

    return (
        <div className="container-app py-10" data-testid="admin-bg-checks-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Compliance</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Background checks
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Every screening — employer-requested and talent-initiated — in one place.
                        {!canSeeDetails && (
                            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[11px] font-semibold border border-amber-500/30">
                                Support view — no PII, no adjudication
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={load} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-refresh">
                        <RefreshCw size={12} /> Refresh
                    </button>
                    {canSeeDetails && (
                        <button type="button" onClick={exportCsv} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-export-csv">
                            <Download size={12} /> Export CSV
                        </button>
                    )}
                    <Link to="/admin/background-checks/disputes" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-disputes-link">
                        <Flag size={12} /> Disputes
                    </Link>
                    {canSeeDetails && (
                        <Link to="/admin/background-checks/retention" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-retention-link">
                            <Trash2 size={12} /> Retention
                        </Link>
                    )}
                    <Link to="/admin/background-checks/age-flags" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="bg-age-flags-link">
                        <ShieldCheck size={12} /> Age gate
                    </Link>
                </div>
            </header>

            {/* Filter bar */}
            <div className="mt-6 card-soft p-4" data-testid="bg-filter-bar">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    <Filter size={11} /> Filters
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-6 gap-2">
                    <div className="md:col-span-2 relative">
                        <SearchIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setPage(1); }}
                            placeholder="Search name, email, employer, ID…"
                            className="w-full rounded-lg border border-border bg-background pl-8 pr-3 py-2 text-sm"
                            data-testid="bg-search"
                        />
                    </div>
                    <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bg-filter-status">
                        <option value="">All statuses</option>
                        {(filters.statuses || []).map((s) => <option key={s.id} value={s.id}>{s.id} ({s.count})</option>)}
                    </select>
                    <select value={pkg} onChange={(e) => { setPkg(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bg-filter-package">
                        <option value="">All packages</option>
                        {(filters.packages || []).map((p) => <option key={p.id} value={p.id}>{p.label}{p.count > 0 ? ` (${p.count})` : ""}</option>)}
                    </select>
                    <select value={employer} onChange={(e) => { setEmployer(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bg-filter-employer">
                        <option value="">All employers</option>
                        {(filters.employers || []).map((e) => <option key={e.id} value={e.id}>{e.company} ({e.count})</option>)}
                    </select>
                    <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bg-filter-source">
                        <option value="">All sources</option>
                        <option value="employer_requested">Employer-requested</option>
                        <option value="candidate_initiated">Talent-initiated</option>
                    </select>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <label className="text-[11px] text-muted-foreground">From</label>
                    <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-2 py-1 text-xs" data-testid="bg-date-from" />
                    <label className="text-[11px] text-muted-foreground">To</label>
                    <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="rounded-lg border border-border bg-background px-2 py-1 text-xs" data-testid="bg-date-to" />
                    <button type="button" onClick={clearAll} className="ml-auto text-xs text-muted-foreground hover:text-foreground underline" data-testid="bg-clear-filters">
                        Clear all
                    </button>
                </div>
            </div>

            {/* Table (desktop) / cards (mobile) */}
            <div className="mt-6">
                {loading ? (
                    <div className="py-14 flex justify-center"><Loader2 className="animate-spin text-emerald-500" /></div>
                ) : rows.length === 0 ? (
                    <div className="card-soft p-14 text-center text-sm text-muted-foreground" data-testid="bg-empty">
                        No background checks match the current filters.
                    </div>
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto rounded-2xl border border-border" data-testid="bg-table">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                    <tr>
                                        <th className="px-4 py-3 font-semibold">Candidate</th>
                                        <th className="px-4 py-3 font-semibold">Employer</th>
                                        <th className="px-4 py-3 font-semibold">Package</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                        <th className="px-4 py-3 font-semibold">Requested</th>
                                        <th className="px-4 py-3 font-semibold">Copy sent</th>
                                        <th className="px-4 py-3 font-semibold text-right">Open</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-card divide-y divide-border">
                                    {rows.map((r) => (
                                        <tr key={r.id} className="hover:bg-muted/30 transition-colors" data-testid={`bg-row-${r.id}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <SourceBadge source={r.source} />
                                                    <Link to={`/admin/users/${r.candidate_id || ""}`} className="font-medium text-foreground hover:underline" data-testid={`bg-row-candidate-${r.id}`}>
                                                        {r.candidate_name || r.candidate_email || "—"}
                                                    </Link>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground">{r.candidate_email}</div>
                                            </td>
                                            <td className="px-4 py-3 text-foreground">{r.employer_company || "—"}</td>
                                            <td className="px-4 py-3 text-muted-foreground">{r.package_label || r.package_id}</td>
                                            <td className="px-4 py-3">
                                                <ResultBadge status={r.status} label={r.result_label} />
                                            </td>
                                            <td className="px-4 py-3 text-muted-foreground text-xs">
                                                {r.requested_at ? new Date(r.requested_at).toLocaleDateString() : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                {r.candidate_copy_sent ? <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Yes</span> : <span className="text-muted-foreground">No</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <Link to={`/admin/background-checks/${r.id}`} className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:underline text-xs font-semibold" data-testid={`bg-row-open-${r.id}`}>
                                                    Open <ArrowUpRight size={11} />
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="md:hidden space-y-3" data-testid="bg-cards">
                            {rows.map((r) => (
                                <Link key={r.id} to={`/admin/background-checks/${r.id}`} className="block card-soft p-4 hover:border-emerald-500/40 transition-colors" data-testid={`bg-card-${r.id}`}>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <SourceBadge source={r.source} />
                                        <ResultBadge status={r.status} label={r.result_label} />
                                        {r.candidate_copy_sent && <span className="text-[10px] text-emerald-600 font-semibold">Copy sent</span>}
                                    </div>
                                    <div className="mt-2 font-heading font-semibold text-foreground">{r.candidate_name || r.candidate_email}</div>
                                    <div className="text-xs text-muted-foreground">{r.employer_company} · {r.package_label || r.package_id}</div>
                                    <div className="mt-1 text-[10px] text-muted-foreground">{r.requested_at ? new Date(r.requested_at).toLocaleString() : ""}</div>
                                </Link>
                            ))}
                        </div>
                    </>
                )}

                {/* Pagination */}
                {total > pageSize && (
                    <div className="mt-4 flex items-center justify-between text-sm" data-testid="bg-pagination">
                        <div className="text-muted-foreground">
                            Page <b className="text-foreground">{page}</b> of <b className="text-foreground">{totalPages}</b> · {total} rows
                        </div>
                        <div className="flex gap-2">
                            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="btn-outline text-xs disabled:opacity-50" data-testid="bg-page-prev">Prev</button>
                            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="btn-outline text-xs disabled:opacity-50" data-testid="bg-page-next">Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
